import { supabase } from './client';
import { isDevelopment } from '@/utils/environment';

// Development Authentication Service
export const devAuthService = {
  // Create a development user with approval
  async createDevUser(email: string, password: string, fullName: string = 'Dev User') {
    try {
      // Step 1: First try to sign in (user might already exist)

      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (!signInError && signInData.user) {
        return { data: signInData, error: null };
      }

      // Step 2: If sign in fails, try to create the user

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            is_dev_user: true
          }
        }
      });

      if (authError) {
        // Handle rate limiting and other errors
        if (
          authError.message.includes('already registered') ||
          authError.message.includes('already been registered')
        ) {
          const { data: retrySignInData, error: retrySignInError } =
            await supabase.auth.signInWithPassword({
              email,
              password
            });

          if (retrySignInError) {
            // Check if it's an email verification error
            if (
              retrySignInError.message.includes('Email not confirmed') ||
              retrySignInError.message.includes('email_not_confirmed')
            ) {
              throw new Error(
                `Email verification required. Please check your email or verify manually in Supabase dashboard for user: ${email}`
              );
            }
            throw new Error(`Sign in failed: ${retrySignInError.message}`);
          }

          return { data: retrySignInData, error: null };
        }

        if (
          authError.message.includes('Too Many Requests') ||
          authError.message.includes('rate limit') ||
          authError.message.includes('you can only request this after')
        ) {
          // Try to sign in with existing credentials
          const { data: existingSignIn, error: existingError } =
            await supabase.auth.signInWithPassword({
              email,
              password
            });

          if (existingError) {
            if (
              existingError.message.includes('Email not confirmed') ||
              existingError.message.includes('email_not_confirmed')
            ) {
              const instructions = this.getEmailVerificationInstructions(email);
              throw new Error(
                `${instructions.quickFix}\n\nInstructions:\n${instructions.instructions.join('\n')}\n\n${instructions.alternative}`
              );
            }
            if (existingError.message.includes('Invalid login credentials')) {
              throw new Error(
                `User ${email} exists but password is incorrect. Please reset the password in Supabase dashboard or use the correct password.`
              );
            }
            throw new Error(`Sign in failed for existing user: ${existingError.message}`);
          }

          return { data: existingSignIn, error: null };
        }

        throw authError;
      }

      if (!authData.user) {
        throw new Error('Failed to create auth user');
      }

      // Step 2: Skip user approval record creation for now (due to policy issues)

      // Step 3: Try to sign in immediately after creation (in case email confirmation is disabled)
      try {
        const { data: immediateSignIn, error: immediateError } =
          await supabase.auth.signInWithPassword({
            email,
            password
          });

        if (!immediateError && immediateSignIn.user) {
          return { data: immediateSignIn, error: null };
        } else {
          if (immediateError) {
            if (
              immediateError.message.includes('Email not confirmed') ||
              immediateError.message.includes('email_not_confirmed')
            ) {
              const instructions = this.getEmailVerificationInstructions(email);
              throw new Error(
                `${instructions.quickFix}\n\nInstructions:\n${instructions.instructions.join('\n')}\n\n${instructions.alternative}`
              );
            }
          }
        }
      } catch (immediateError) {}

      return { data: authData, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Check if we're in development mode
  isDevelopmentMode(): boolean {
    return isDevelopment();
  },

  // Check if auto-login should be enabled
  shouldAutoLogin(): boolean {
    return this.isDevelopmentMode() && import.meta.env['VITE_AUTO_LOGIN'] !== 'false';
  },

  // Get development user credentials
  getDevCredentials() {
    return {
      email: import.meta.env['VITE_DEV_EMAIL'] || 'ucancallmebiru@gmail.com',
      password: import.meta.env['VITE_DEV_PASSWORD'] || 'password123',
      fullName: import.meta.env['VITE_DEV_NAME'] || 'B Ru'
    };
  },

  // Helper function to check user status
  async checkUserStatus(email: string) {
    try {
      // Try to get user info (this might not work due to RLS, but worth trying)
      const { data: users, error } = await supabase
        .from('user_approvals')
        .select('*')
        .eq('email', email)
        .single();

      if (error) {
        return null;
      }

      return users;
    } catch (error) {
      return null;
    }
  },

  // Helper function to provide instructions for manual email verification
  getEmailVerificationInstructions(email: string) {
    return {
      message: `Email verification required for ${email}`,
      instructions: [
        '1. Go to your Supabase dashboard: https://supabase.com/dashboard',
        '2. Select your project: exycbmlolhxpacnyqvmw',
        '3. Navigate to Authentication > Users',
        '4. Find the user with email: ' + email,
        '5. Click on the user row',
        '6. In the user details, find "Email Confirmed" and set it to "true"',
        '7. Click "Save" to update the user'
      ],
      alternative:
        'Alternatively, you can disable email confirmation in Authentication > Settings > "Enable email confirmations" (turn it off for development)',
      quickFix:
        'The user was created successfully, but needs email confirmation in Supabase dashboard.'
    };
  }
};
